import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import { UserLoggedInNavigatorScreenProps } from "@/src/TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { withWebModalStack } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModalStack";
import { useCallback } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { WorkspaceTodoEditScheduledArchiveAtQuery } from "./__generated__/WorkspaceTodoEditScheduledArchiveAtQuery.graphql";
import { useWorkspaceTodoArchiveDescription } from "./useWorkspaceTodoArchiveDescription";
import { WorkspaceEditScheduledArchiveAt } from "../../WorkspaceTodoSignalShared/WorkspaceEditScheduledArchiveAt";
import { DateTime } from "luxon";

function InnerWorkspaceTodoEditScheduledArchiveAt(
  props: UserLoggedInNavigatorScreenProps<"WorkspaceTodoEditScheduledArchiveAt"> & {
    fetchKey: string;
  }
) {
  const data = useLazyLoadQuery<WorkspaceTodoEditScheduledArchiveAtQuery>(
    graphql`
      query WorkspaceTodoEditScheduledArchiveAtQuery($workspaceTodoId: ID!) {
        todoWorkspace(id: $workspaceTodoId) {
          scheduledArchiveAt {
            isoString
            ...FormattedDate
          }
        }
      }
    `,
    { workspaceTodoId: props.route.params.workspaceTodoId },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const [save, saveLoading] = useMutation(graphql`
    mutation WorkspaceTodoEditScheduledArchiveAtSaveMutation(
      $workspaceTodoId: ID!
      $scheduledArchiveAt: String
    ) {
      todoWorkspace(id: $workspaceTodoId) {
        updateScheduledArchiveAt(newArchiveAt: $scheduledArchiveAt) {
          id
        }
      }
    }
  `);

  const [archive, archiveLoading] = useMutation(graphql`
    mutation WorkspaceTodoEditScheduledArchiveAtArchiveMutation(
      $workspaceTodoId: ID!
    ) {
      todoWorkspace(id: $workspaceTodoId) {
        archive {
          id
          archivedAtDate {
            ...FormattedDate
          }
        }
      }
    }
  `);

  const onSave = useCallback(
    (archiveDate: DateTime | null) => {
      save({
        variables: {
          workspaceTodoId: props.route.params.workspaceTodoId,
          scheduledArchiveAt: archiveDate?.toISO() ?? null,
        },
        onCompleted: () => {
          props.navigation.goBack();
        },
      });
    },
    [props.route.params.workspaceTodoId, save, props.navigation]
  );

  const onArchive = useCallback(() => {
    archive({
      variables: {
        workspaceTodoId: props.route.params.workspaceTodoId,
      },
      onCompleted: () => {
        props.navigation.pop();
        try {
          props.navigation.replace("WorkspaceTodosArchived" as any, {
            workspaceId: props.route.params.workspaceId,
          });
        } catch (error) {
          console.error(error);
        }
      },
    });
  }, [
    archive,
    props.route.params.workspaceTodoId,
    props.route.params.workspaceId,
    props.navigation,
  ]);

  const archiveDescription = useWorkspaceTodoArchiveDescription();

  return (
    <WorkspaceEditScheduledArchiveAt
      close={props.navigation.goBack}
      setOptions={props.navigation.setOptions}
      description={archiveDescription}
      onArchive={onArchive}
      archiveLoading={archiveLoading}
      onSave={onSave}
      saveLoading={saveLoading}
      initialArchiveAt={data.todoWorkspace?.scheduledArchiveAt?.isoString}
    />
  );
}

function WorkspaceTodoEditScheduledArchiveAtC(
  props: UserLoggedInNavigatorScreenProps<"WorkspaceTodoEditScheduledArchiveAt">
) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => (
        <InnerWorkspaceTodoEditScheduledArchiveAt
          fetchKey={fetchKey}
          {...props}
        />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

export const WorkspaceTodoEditScheduledArchiveAt = withWebModalStack(
  WorkspaceTodoEditScheduledArchiveAtC,
  true,
  {
    height: 350,
    width: 600,
  }
);
